import React from "react";
import { BookOpenText, KeyRound, Link2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";

const renderType = (typeValue) => {
  if (Array.isArray(typeValue)) {
    return typeValue.join(" | ");
  }
  return typeValue || "string";
};

function TableSchemaPanel({ table }) {
  const schema = table?.schema;
  const properties = Object.entries(schema?.properties || {});

  if (!table || !schema) {
    return null;
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>{schema.title || table.table}</CardTitle>
            {schema.description && (
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{schema.description}</p>
            )}
          </div>
          <div className="rounded-xl bg-blue-50 p-3 text-blue-600 dark:bg-blue-950/50 dark:text-blue-300">
            <BookOpenText className="h-5 w-5" />
          </div>
        </CardHeader>
        <CardContent className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 dark:border-gray-800 dark:bg-gray-950">
            <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Columns</div>
            <div className="mt-1 text-xl font-semibold text-gray-900 dark:text-gray-100">{properties.length}</div>
          </div>
          <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 dark:border-gray-800 dark:bg-gray-950">
            <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Required</div>
            <div className="mt-1 text-xl font-semibold text-gray-900 dark:text-gray-100">{schema.required?.length || 0}</div>
          </div>
          <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 dark:border-gray-800 dark:bg-gray-950">
            <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Unique Keys</div>
            <div className="mt-1 text-xl font-semibold text-gray-900 dark:text-gray-100">{schema.unique_keys?.length || 0}</div>
          </div>
          <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 dark:border-gray-800 dark:bg-gray-950">
            <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Create</div>
            <div className="mt-1 text-xl font-semibold text-gray-900 dark:text-gray-100">
              {schema.create_disabled ? "Disabled" : "Enabled"}
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-3 xl:grid-cols-2">
        {properties.map(([fieldName, fieldSchema]) => (
          <Card key={fieldName} className="h-full">
            <CardContent className="space-y-3 pt-5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">{fieldName}</div>
                  <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">{renderType(fieldSchema.type)}</div>
                </div>
                <div className="flex items-center gap-2">
                  {fieldSchema.primary_key && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-1 text-[11px] font-medium text-amber-700 dark:bg-amber-950/60 dark:text-amber-300">
                      <KeyRound className="h-3 w-3" />
                      PK
                    </span>
                  )}
                  {fieldSchema.foreign_key && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-1 text-[11px] font-medium text-blue-700 dark:bg-blue-950/60 dark:text-blue-300">
                      <Link2 className="h-3 w-3" />
                      FK
                    </span>
                  )}
                </div>
              </div>

              <p className="text-sm text-gray-600 dark:text-gray-300">
                {fieldSchema.description || "No description provided."}
              </p>

              {fieldSchema.foreign_key && (
                <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600 dark:border-gray-800 dark:bg-gray-950 dark:text-gray-300">
                  References `{fieldSchema.foreign_key.table}.{fieldSchema.foreign_key.column}`
                </div>
              )}

              {Array.isArray(fieldSchema.enum) && fieldSchema.enum.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {fieldSchema.enum.map((option) => (
                    <span
                      key={`${fieldName}-${option}`}
                      className="rounded-full bg-gray-100 px-2 py-1 text-[11px] text-gray-700 dark:bg-gray-800 dark:text-gray-300"
                    >
                      {option}
                    </span>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

export default TableSchemaPanel;
